import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { safeGetItem } from '../utils/safeStorage';

const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * Decodes a JWT and returns the expiration timestamp (seconds).
 * Returns null if the token is invalid or missing the exp claim.
 */
function getTokenExp(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Hook that monitors the access token expiry and shows a warning
 * toast 5 minutes before it expires, with a "Stay Logged In" button
 * that proactively refreshes the token.
 */
export function useSessionTimer() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismissWarning = useCallback(() => {
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, []);

  const handleStayLoggedIn = useCallback(async () => {
    dismissWarning();
    try {
      const refreshToken = safeGetItem('refresh_token');
      if (!refreshToken) return;

      // Use the same refresh endpoint the interceptor uses
      // NOTE: REACT_APP_API_URL is the base (e.g. http://host:8000), must append /api
      const { default: axios } = await import('axios');
      const baseUrl = process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL}/api`
        : 'http://localhost:8000/api';
      const { data } = await axios.post(`${baseUrl}/auth/refresh`, {
        refresh_token: refreshToken,
      });

      // Store new tokens and notify the timer to reschedule
      const { safeSetItem } = await import('../utils/safeStorage');
      safeSetItem('access_token', data.access_token);
      safeSetItem('refresh_token', data.refresh_token);

      // Dispatch synthetic StorageEvent so the listener reschedules the timer
      // (StorageEvent doesn't fire for same-tab writes natively)
      window.dispatchEvent(new StorageEvent('storage', { key: 'access_token' }));

      toast.success('Session extended successfully');
    } catch {
      toast.error('Failed to extend session. Please log in again.');
    }
  }, [dismissWarning]);

  const showWarning = useCallback(() => {
    // Don't stack multiple warnings
    if (toastIdRef.current) return;

    toastIdRef.current = toast(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px' }}>Your session expires soon.</span>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toastIdRef.current = null;
              handleStayLoggedIn();
            }}
            style={{
              background: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Stay Logged In
          </button>
        </div>
      ),
      {
        duration: Infinity,
        icon: '\u23f0',
        style: { maxWidth: '420px' },
      }
    );
  }, [handleStayLoggedIn]);

  const scheduleWarning = useCallback(() => {
    clearTimer();
    dismissWarning();

    const token = safeGetItem('access_token');
    const exp = getTokenExp(token);
    if (!exp) return;

    const expiresAtMs = exp * 1000;
    const now = Date.now();
    const remainingMs = expiresAtMs - now;

    // Token already expired or less than warning threshold
    if (remainingMs <= 0) return;

    if (remainingMs <= WARNING_BEFORE_MS) {
      // Already inside the warning window — show immediately
      showWarning();
      return;
    }

    // Schedule warning for (expiry - 5 min)
    const delay = remainingMs - WARNING_BEFORE_MS;
    timerRef.current = setTimeout(showWarning, delay);
  }, [clearTimer, dismissWarning, showWarning]);

  useEffect(() => {
    // Initial schedule
    scheduleWarning();

    // Re-schedule when tokens change (e.g. after auto-refresh by interceptor)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        scheduleWarning();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Also poll periodically in case the interceptor refreshes in the same tab
    // (StorageEvent doesn't fire for same-tab writes)
    const pollInterval = setInterval(() => {
      const token = safeGetItem('access_token');
      const exp = getTokenExp(token);
      if (!exp) return;

      const remainingMs = exp * 1000 - Date.now();

      // If token was refreshed (e.g. by axios interceptor) and warning is now stale, dismiss it
      if (remainingMs > WARNING_BEFORE_MS && toastIdRef.current) {
        dismissWarning();
        clearTimer();
        scheduleWarning(); // Reschedule for the new token
        return;
      }

      if (remainingMs <= WARNING_BEFORE_MS && remainingMs > 0 && !toastIdRef.current) {
        showWarning();
      }
    }, 60_000); // Check every minute

    return () => {
      clearTimer();
      dismissWarning();
      window.removeEventListener('storage', handleStorage);
      clearInterval(pollInterval);
    };
  }, [scheduleWarning, clearTimer, dismissWarning, showWarning]);
}
